/* Variante B · Rollen-Änderungs-Modal (nur für Owner nutzbar).
 * Radio-Auswahl aus (Support / Moderator / Admin / Kein Zugriff).
 * Owner-Vergabe ist gesperrt (nur EIN Owner erlaubt, Transfer separat).
 */
import { useState } from "react";
import { X, ShieldCheck, ShieldAlert, Shield, Eye, User } from "lucide-react";
import api from "@/lib/api";

const ROLE_OPTIONS = [
  { value: null,          label: "Kein Admin-Zugang",  desc: "Normaler User ohne Backoffice-Rechte", icon: User,        variant: "text-[#666]" },
  { value: "support",     label: "Support",             desc: "Read-Only auf Orders, Impersonate, 2FA-Reset", icon: Eye, variant: "text-blue-600" },
  { value: "moderator",   label: "Moderator",           desc: "Content-Moderation, Reports, User-Suspends", icon: Shield, variant: "text-emerald-600" },
  { value: "admin",       label: "Admin",               desc: "Voll-Admin: User + Settings + Finance + Moderation", icon: ShieldCheck, variant: "text-purple-600" },
];

export default function RoleChangeModal({ open, user, onClose, onDone }) {
  const [selected, setSelected] = useState(user?.role || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!open || !user) return null;

  const isTargetOwner = user.role === "owner";
  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/users/${user.user_id || user._id}/role`, { role: selected });
      onDone?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Speichern der Rolle");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="role-change-modal"
      className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-[#0A0A0A]" />
            <p className="text-sm font-bold">Rolle ändern</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="role-modal-close"
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
          <p className="text-[10px] uppercase tracking-wider text-[#999] font-semibold">User</p>
          <p className="text-sm font-medium text-[#0A0A0A] truncate">{user.email}</p>
          <p className="text-[11px] text-[#666] truncate">{user.display_name || user.handle || ""}</p>
        </div>

        {isTargetOwner ? (
          <div className="p-6 text-center">
            <ShieldCheck size={32} className="text-amber-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-[#0A0A0A]">System-Owner</p>
            <p className="text-xs text-[#666] mt-1">Die Rolle des System-Owners kann nicht über diesen Dialog geändert werden. Für einen Ownership-Transfer bitte separat kontaktieren.</p>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto flex-1 divide-y divide-[#F0F0F0]">
              {ROLE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = selected === opt.value;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => setSelected(opt.value)}
                    data-testid={`role-option-${opt.value || "none"}`}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${active ? "bg-[#FAFAFA]" : "hover:bg-[#FAFAFA]"}`}
                  >
                    <Icon size={16} className={`shrink-0 mt-0.5 ${opt.variant}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0A0A0A]">{opt.label}</p>
                      <p className="text-[11px] text-[#666] mt-0.5">{opt.desc}</p>
                    </div>
                    <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#999]"}`}>
                      {active ? "✓" : ""}
                    </span>
                  </button>
                );
              })}
            </div>

            {error && <p className="px-4 py-2 text-xs text-red-500" data-testid="role-modal-error">{error}</p>}

            <div className="border-t border-[#E5E5E5] px-4 py-3 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                data-testid="role-modal-cancel"
                className="h-10 px-5 rounded-full border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#FAFAFA]"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="role-modal-save"
                className="flex-1 h-10 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold disabled:opacity-40 hover:bg-[#222]"
              >
                {saving ? "Speichere …" : "Speichern"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
